import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useCurrency } from '@/hooks/useCurrency';
import { useCart } from '@/hooks/useCart';

const navLinks = [
  { key: 'nav.home', href: '/' },
  { key: 'nav.packaging', href: '/packaging' },
  { key: 'nav.howItWorks', href: '/how-it-works' },
  { key: 'nav.supplierNetwork', href: '/supplier-network' },
  { key: 'nav.aboutUs', href: '/about' },
  { key: 'nav.contact', href: '/contact' },
];

const languages = [
  { code: 'en', label: 'English' },
  { code: 'zh', label: '中文' },
  { code: 'es', label: 'Español' },
  { code: 'ja', label: '日本語' },
  { code: 'ar', label: 'العربية' },
  { code: 'pt', label: 'Português' },
  { code: 'ru', label: 'Русский' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'hi', label: 'हिन्दी' },
  { code: 'ko', label: '한국어' },
  { code: 'vi', label: 'Tiếng Việt' },
  { code: 'tr', label: 'Türkçe' },
  { code: 'th', label: 'ไทย' },
];

const currencies = ['USD', 'CNY', 'EUR', 'JPY', 'AED', 'BRL', 'RUB', 'INR', 'KRW', 'VND', 'TRY', 'THB', 'SGD'];

interface DropdownItem {
  label: string;
  href: string;
  icon: string;
  divider?: boolean;
  adminOnly?: boolean;
  onClick?: () => void;
}

export default function Navbar() {
  const { t, i18n } = useTranslation(['home', 'auth']);
  const { user, profile, company, signOut } = useAuth();
  const { currency: currentCurrency, setCurrency: setCurrentCurrency } = useCurrency();
  const { count: cartCount } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [unreadCount] = useState(0);

  const langRef = useRef<HTMLDivElement>(null);
  const currencyRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(event.target as Node)) {
        setLangOpen(false);
      }
      if (currencyRef.current && !currencyRef.current.contains(event.target as Node)) {
        setCurrencyOpen(false);
      }
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const changeLanguage = (code: string) => {
    localStorage.setItem('i18nextLng', code);
    i18n.changeLanguage(code);
    setLangOpen(false);
  };

  const changeCurrency = (curr: string) => {
    setCurrentCurrency(curr);
    setCurrencyOpen(false);
  };

  const currentLang = languages.find((l) => l.code === i18n.language) || languages[0];

  const displayName =
    company?.company_name ||
    profile?.full_name ||
    user?.email?.split('@')[0] ||
    'User';

  const email = user?.email || '';
  const avatarInitial = (displayName || email || 'U').charAt(0).toUpperCase();

  const isAdmin = profile?.role === 'admin' || profile?.role === 'super_admin';
  const isSuperAdmin = profile?.role === 'super_admin';

  const dropdownItems: DropdownItem[] = [
    { label: t('auth:dashboard'), href: '/my', icon: 'ri-dashboard-line' },
    { label: t('auth:myQuotes'), href: '/my/quotes', icon: 'ri-file-list-3-line' },
    { label: t('auth:myOrders'), href: '/my/orders', icon: 'ri-truck-line' },
    { label: t('auth:aiPreviews'), href: '/my/finish-previews', icon: 'ri-magic-line' },
    { label: t('auth:savedProducts'), href: '/my/favorites', icon: 'ri-heart-line' },
    { label: '', href: '', icon: '', divider: true },
    { label: t('auth:companyProfile'), href: '/my/company', icon: 'ri-building-4-line' },
    { label: t('auth:shippingAddress'), href: '/my/addresses', icon: 'ri-map-pin-line' },
    { label: t('auth:accountSettings'), href: '/my/settings', icon: 'ri-settings-3-line' },
    { label: '', href: '', icon: '', divider: true },
    {
      label: t('auth:adminConsole'),
      href: '/admin',
      icon: 'ri-shield-user-line',
      adminOnly: true,
    },
    {
      label: t('auth:logout'),
      href: '#',
      icon: 'ri-logout-box-r-line',
      onClick: () => {
        signOut().catch(() => {});
        setDropdownOpen(false);
      },
    },
  ];

  const visibleDropdownItems = dropdownItems.filter(
    (item) => !item.adminOnly || isAdmin || isSuperAdmin
  );

  return (
    <div className="relative w-full z-50 transition-all duration-300 bg-white border-b border-ink-100">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-[68px]">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2.5 shrink-0">
            <div className="w-8 h-8 bg-ink-700 flex items-center justify-center">
              <i className="ri-box-3-line text-white text-base" />
            </div>
            <span className="text-lg md:text-xl font-bold tracking-tight text-ink-700">
              GBCOSPACK
            </span>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden xl:flex items-center gap-0.5">
            {navLinks.map((link) => (
              <a
                key={link.key}
                href={link.href}
                className="px-3 py-2 text-sm font-semibold transition-colors whitespace-nowrap text-ink-500 hover:text-ink-700 hover:bg-cream-50"
              >
                {t(link.key)}
              </a>
            ))}
          </nav>

          {/* Right Actions */}
          <div className="hidden lg:flex items-center gap-1">
            {/* Language */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => { setLangOpen(!langOpen); setCurrencyOpen(false); }}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs transition-colors whitespace-nowrap text-ink-500 hover:text-ink-700 hover:bg-cream-50"
              >
                <i className="ri-global-line" />
                <span>{currentLang.label}</span>
                <i className={`ri-arrow-down-s-line text-[10px] transition-transform ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              {langOpen && (
                <div className="absolute top-full right-0 mt-1 w-52 bg-white border border-ink-100 py-1 z-50 shadow-lg">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-cream-50 transition-colors ${
                        i18n.language === lang.code ? 'text-warm-500 font-medium' : 'text-ink-500'
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Currency */}
            <div className="relative" ref={currencyRef}>
              <button
                onClick={() => { setCurrencyOpen(!currencyOpen); setLangOpen(false); }}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs transition-colors whitespace-nowrap text-ink-500 hover:text-ink-700 hover:bg-cream-50"
              >
                <i className="ri-coins-line" />
                <span>{currentCurrency}</span>
                <i className={`ri-arrow-down-s-line text-[10px] transition-transform ${currencyOpen ? 'rotate-180' : ''}`} />
              </button>
              {currencyOpen && (
                <div className="absolute top-full right-0 mt-1 w-36 bg-white border border-ink-100 py-1 z-50 shadow-lg">
                  {currencies.map((curr) => (
                    <button
                      key={curr}
                      onClick={() => changeCurrency(curr)}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-cream-50 transition-colors ${
                        currentCurrency === curr ? 'text-warm-500 font-medium' : 'text-ink-500'
                      }`}
                    >
                      {curr}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Cart Icon */}
            <a
              href="/cart"
              className="relative flex items-center gap-1 px-2.5 py-1.5 text-xs transition-colors whitespace-nowrap text-ink-500 hover:text-ink-700 hover:bg-cream-50"
            >
              <i className="ri-shopping-cart-2-line text-base" />
              {cartCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-warm-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full leading-none">
                  {cartCount > 99 ? '99+' : cartCount}
                </span>
              )}
            </a>

            <div className="w-px h-5 bg-ink-200 mx-1" />

            {/* Auth */}
            {user ? (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-2 px-2.5 py-1.5 text-sm transition-colors whitespace-nowrap text-ink-500 hover:text-ink-700 hover:bg-cream-50"
                >
                  <div className="relative">
                    {profile?.avatar_url ? (
                      <img
                        src={profile.avatar_url}
                        alt=""
                        className="w-7 h-7 object-cover"
                      />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-ink-100 flex items-center justify-center text-xs font-bold text-ink-600">
                        {avatarInitial}
                      </div>
                    )}
                    {unreadCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-3.5 bg-red-500 text-white text-[9px] font-bold flex items-center justify-center px-0.5">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </div>
                  <span className="max-w-[120px] truncate font-medium">{displayName}</span>
                  <i className={`ri-arrow-down-s-line text-[10px] transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full right-0 mt-2 w-64 bg-white border border-ink-100 py-2 z-50 shadow-xl">
                    {/* User Card */}
                    <div className="px-4 py-3 flex items-start gap-3 border-b border-ink-100 mb-1">
                      <div className="relative shrink-0">
                        {profile?.avatar_url ? (
                          <img
                            src={profile.avatar_url}
                            alt=""
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-ink-100 flex items-center justify-center text-sm font-bold text-ink-600">
                            {avatarInitial}
                          </div>
                        )}
                        {unreadCount > 0 && (
                          <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-red-500 border-2 border-white" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-ink-700 truncate">{displayName}</p>
                        <p className="text-xs text-ink-400 truncate">{email}</p>
                        <div className="flex items-center gap-1.5 mt-1">
                          <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium bg-cream-100 text-ink-500 border border-ink-100">
                            {t('auth:customerAccount')}
                          </span>
                          {isSuperAdmin && (
                            <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium bg-ink-700 text-white">
                              {t('auth:adminBadge')}
                            </span>
                          )}
                          {isAdmin && !isSuperAdmin && (
                            <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium bg-ink-500 text-white">
                              {t('auth:adminBadge')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Menu Items */}
                    {visibleDropdownItems.map((item, idx) => {
                      if (item.divider) {
                        return <div key={`div-${idx}`} className="border-t border-ink-100 my-1" />;
                      }
                      if (item.onClick) {
                        return (
                          <button
                            key={item.label}
                            onClick={item.onClick}
                            className="w-full text-left px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors flex items-center gap-2.5"
                          >
                            <i className={`${item.icon} text-base`} />
                            {item.label}
                          </button>
                        );
                      }
                      return (
                        <a
                          key={item.label}
                          href={item.href}
                          onClick={() => setDropdownOpen(false)}
                          className="block px-4 py-2.5 text-sm text-ink-500 hover:bg-cream-50 hover:text-ink-700 transition-colors flex items-center gap-2.5"
                        >
                          <i className={`${item.icon} text-base text-ink-400`} />
                          <span className="font-medium">{item.label}</span>
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <a
                href="/sign-in"
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap text-ink-500 hover:text-ink-700 hover:bg-cream-50"
              >
                <i className="ri-user-line text-base" />
                <span>{t('home:nav.signIn')}</span>
              </a>
            )}
          </div>

          {/* Mobile Right Actions */}
          <div className="flex items-center gap-2 lg:hidden">
            {/* Cart Icon - Mobile */}
            <a
              href="/cart"
              onClick={() => setMobileOpen(false)}
              className="relative p-1.5 text-ink-700 hover:bg-cream-50 transition-colors"
            >
              <i className="ri-shopping-cart-2-line text-xl" />
              {cartCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-warm-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full leading-none">
                  {cartCount > 99 ? '99+' : cartCount}
                </span>
              )}
            </a>
            {user && (
              <a
                href="/my"
                onClick={() => setMobileOpen(false)}
                className="relative p-1.5 text-ink-700 hover:bg-cream-50 transition-colors"
              >
                {profile?.avatar_url ? (
                  <img src={profile.avatar_url} alt="" className="w-7 h-7 object-cover" />
                ) : (
                  <div className="w-7 h-7 bg-ink-100 flex items-center justify-center text-xs font-bold text-ink-600">
                    {avatarInitial}
                  </div>
                )}
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-3.5 bg-red-500 text-white text-[9px] font-bold flex items-center justify-center px-0.5">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </a>
            )}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="p-2 text-ink-700 hover:bg-cream-50 transition-colors"
            >
              <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl`} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-ink-100 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <a
                key={link.key}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className="block px-3 py-2.5 text-sm font-medium text-ink-600 hover:bg-cream-50"
              >
                {t(link.key)}
              </a>
            ))}
            <div className="border-t border-ink-100 pt-3 mt-3 space-y-2">
              <div className="flex items-center gap-4 px-3">
                <span className="text-xs text-ink-400 uppercase tracking-wider">{t('home:nav.language')}</span>
                <div className="flex flex-wrap gap-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`px-2 py-1 text-xs border ${
                        i18n.language === lang.code
                          ? 'border-warm-500 text-warm-500 bg-warm-50'
                          : 'border-ink-200 text-ink-400'
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-4 px-3">
                <span className="text-xs text-ink-400 uppercase tracking-wider">{t('home:nav.currency')}</span>
                <div className="flex flex-wrap gap-2">
                  {currencies.map((curr) => (
                    <button
                      key={curr}
                      onClick={() => changeCurrency(curr)}
                      className={`px-2 py-1 text-xs border ${
                        currentCurrency === curr
                          ? 'border-warm-500 text-warm-500 bg-warm-50'
                          : 'border-ink-200 text-ink-400'
                      }`}
                    >
                      {curr}
                    </button>
                  ))}
                </div>
              </div>
              {user ? (
                <div className="px-3 pt-2 space-y-1">
                  {/* Mobile User Card */}
                  <div className="flex items-center gap-3 py-3 border-b border-ink-100 mb-1">
                    <div className="relative shrink-0">
                      {profile?.avatar_url ? (
                        <img src={profile.avatar_url} alt="" className="w-10 h-10 object-cover" />
                      ) : (
                        <div className="w-10 h-10 bg-ink-100 flex items-center justify-center text-sm font-bold text-ink-600">
                          {avatarInitial}
                        </div>
                      )}
                      {unreadCount > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-red-500 border-2 border-white" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-ink-700 truncate">{displayName}</p>
                      <p className="text-xs text-ink-400 truncate">{email}</p>
                      <div className="flex items-center gap-1.5 mt-1">
                        <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium bg-cream-100 text-ink-500 border border-ink-100">
                          {t('auth:customerAccount')}
                        </span>
                        {isSuperAdmin && (
                          <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium bg-ink-700 text-white">
                            {t('auth:adminBadge')}
                          </span>
                        )}
                        {isAdmin && !isSuperAdmin && (
                          <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium bg-ink-500 text-white">
                            {t('auth:adminBadge')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {visibleDropdownItems.map((item, idx) => {
                    if (item.divider) {
                      return <div key={`mob-div-${idx}`} className="border-t border-ink-100 my-1" />;
                    }
                    if (item.onClick) {
                      return (
                        <button
                          key={item.label}
                          onClick={() => {
                            item.onClick?.();
                            setMobileOpen(false);
                          }}
                          className="w-full text-left px-3 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2.5"
                        >
                          <i className={`${item.icon} text-base`} />
                          {item.label}
                        </button>
                      );
                    }
                    return (
                      <a
                        key={item.label}
                        href={item.href}
                        onClick={() => setMobileOpen(false)}
                        className="block px-3 py-2.5 text-sm text-ink-600 hover:bg-cream-50 flex items-center gap-2.5"
                      >
                        <i className={`${item.icon} text-base text-ink-400`} />
                        <span className="font-medium">{item.label}</span>
                      </a>
                    );
                  })}
                </div>
              ) : (
                <a
                  href="/sign-in"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-ink-600 hover:bg-cream-50"
                >
                  <i className="ri-user-line text-base" />
                  {t('home:nav.signIn')}
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}